/* @flow */

import * as _ from "lodash";
import { default as moment } from "moment";

import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";

import ArticleStore from "./ArticleStore";
import * as BundleSelectionUtil from "../utils/BundleSelectionUtil";
import { trackSearchResult } from "../utils/SearchTrackingUtil";
import TrackingUtil from "shared/src/utils/TrackingUtil";
import {
    mapToCountedIds,
    enhanceWithOfferInfo,
    buildFilters,
    getSearchResultRows,
    DEFAULT_ROWS
} from "../utils/SearchUtil";
import ArticleSearchClient from "../clients/ArticleSearchClient";
import TimeUtil from "../utils/TimeUtil";
import { notify } from "shared/src/utils/Notifications";
import { Store } from "shared/src/stores/Store";
import { determineSearchProfile } from "../utils/determineSearchProfile";

import {
    SearchQuery,
    SearchResultState,
    SearchResult,
    SearchResultEx,
    SearchResultItem,
    SearchResultEntry
} from "shared/src/types/articlesearch/SearchResult";
import { RequestSearchQuery } from "../clients/ArticleSearchClient";
import { isSearchDebugEnabled } from "../searchdiscover/toggles";
import { Bundle } from "shared/src/types/articlesearch/Article";

function getInitialSearchResult(): SearchResultState {
    return {
        q: "",
        searchPurpose: null,

        valid: false,
        searchRunning: false,
        loadingMore: false,
        gotArticles: false,

        categoryPath: null,
        facets: [],
        ranges: [],
        options: [],
        profile: undefined,
        role: undefined,
        sorting: undefined,

        result: null
    };
}

function getInitialQuery() {
    return { requestQuery: null, timeStamp: null, data: null };
}

export type SearchStoreQuery = {
    page: number;
    rows: number;
    more: boolean;
    customerId?: string;
    loggedIn: boolean;
    country: string;
    locale: string;
    selectedStoreId: string;
} & SearchQuery;

let state: SearchResultState;
let lastQuery: {
    requestQuery: RequestSearchQuery | undefined | null;
    timeStamp: any | undefined | null;
    data: SearchStoreQuery | undefined | null;
};

class SearchStore extends Store {
    searchPurpose: "search";
    categoryPurpose: "category";
    replacementPurpose: "replacement";
    promotionsPurpose: "promotions";
    offersPurpose: "offers";
    personallistPurpose: "personallistPurpose";

    constructor() {
        super();
        this.searchPurpose = "search";
        this.categoryPurpose = "category";
        this.replacementPurpose = "replacement";
        this.promotionsPurpose = "promotions";
        this.personallistPurpose = "personallistPurpose";
        this.offersPurpose = "offers";
        this.listenTo(ArticleStore, this._handleArticleUpdate);
        state = getInitialSearchResult();
        lastQuery = getInitialQuery();
    }

    // for testing
    _reset() {
        state = getInitialSearchResult();
        lastQuery = getInitialQuery();
    }

    getState(): SearchResultState {
        return state;
    }

    doSearch(data: SearchStoreQuery) {
        state.searchPurpose = data.searchPurpose;
        state.q = data.q;
        if (data.q) {
            state.sorting = data.sorting;
            state.loadingMore = data.more || false;
            state.categoryPath = data.categoryPath;
            state.facets = data.facets;
            state.ranges = data.ranges;
            state.options = data.options;
            state.restrictionId = data.restrictionId;
            state.onlySearchable = data.onlySearchable;
            state.profile = data.profile;
            state.role = data.role;

            // If we do not do this and the SessionStore triggers fast twice,
            // the second update is lost because in sessionStoreUpdate lastQuery would
            // still be undefined (as the first request is maybe not fulfilled yet).
            lastQuery.data = data;

            const requestQuery = this._buildRequestQuery(data);
            // not clear how to test here for inequality of the request, has to be refactored???
            if (
                !_.isEqual(requestQuery, lastQuery.requestQuery) ||
                TimeUtil.moreThanAmountOfMinutesAgo(lastQuery.timeStamp, 5)
            ) {
                this._performSearchRequest(requestQuery, data);
            }
            this.trigger(state);
        } else {
            state = getInitialSearchResult();
            state.q = data.q;
            this.trigger(state);
        }
    }

    _buildRequestQuery(data: SearchStoreQuery): RequestSearchQuery {
        const page = data.page ? data.page : 1;
        const rows = data.rows ? data.rows : DEFAULT_ROWS;

        const profile = determineSearchProfile(data.profile);
        const filter = buildFilters(data);

        const requestQuery: RequestSearchQuery = {
            storeId: data.selectedStoreId,
            language: data.locale,
            country: data.country,
            query: data.q,
            rows: rows,
            page: page,
            sorting: data.sorting,
            filter,
            profile,
            role: data.role,
            restriction: data.restrictionId,
            onlySearchable: data.onlySearchable,
            facets: true,
            categories: true
        };

        if (isSearchDebugEnabled()) {
            requestQuery.debugQuery = true;
        }

        if (data.loggedIn) {
            requestQuery.customerId = data.customerId;
        }

        return requestQuery;
    }

    _performSearchRequest(requestQuery: RequestSearchQuery, data: SearchStoreQuery) {
        state.searchRunning = true;
        state.gotArticles = false;
        // The loading state should be triggered to reflect that in the listening components.
        // This worked accidentally before because the SearchResult component shows loading state if there are
        // not results yet or search is still running. For two requests with different storeIds
        // the search results of the first search are still shown while second requests still finishes.
        this.trigger(state);
        ArticleSearchClient.doSearch(requestQuery)
            .then((response) => this._doSearchCompleted(response, requestQuery, data))
            .catch(() => this._doSearchFailed());
    }

    _saveSearchResult(
        requestQuery: RequestSearchQuery,
        result: SearchResultEx,
        reusedSearch: boolean,
        data: SearchStoreQuery
    ) {
        lastQuery = { requestQuery: requestQuery, timeStamp: moment(), data: data };

        state.searchRunning = false;
        state.loadingMore = false;
        state.valid = true;
        state.gotArticles = false;

        state.result = result;

        if (!reusedSearch) {
            const { amount, usedOrSearch, ltrExternalModel } = result;
            const pageNumber = Math.floor((result.rows || 0) / getSearchResultRows());
            const sorting = requestQuery.sorting || "relevance";
            // data.q refers to the search term entered by a user and result.query is the search term that solr-search
            // has actually been performed on after potential auto-correction of user's search term
            trackSearchResult(data.q, amount, sorting, pageNumber, usedOrSearch, ltrExternalModel, result.query);
        }
        this._queryArticles();
    }

    _doSearchCompleted(response: SearchResult, requestQuery: RequestSearchQuery, data: SearchStoreQuery) {
        const { result, reusedSearch } = this.acceptSearchResult(response, state.result, state.loadingMore);

        this._saveSearchResult(requestQuery, result, reusedSearch, data);
    }

    acceptSearchResult(response: SearchResult, currentState: SearchResultEx | undefined | null, loadMore: boolean) {
        let additionalVariantIds = [] as string[];
        if (!_.isNil(response.additionalVariantIds)) {
            additionalVariantIds = response.additionalVariantIds;
        }
        let result: SearchResultEx;
        let reusedSearch;
        if (!currentState || _.isEqual(currentState, {}) || !currentState.resultIds || !loadMore) {
            result = Object.assign({}, response, { result: [], idSearchPosition: {} });
            result.additionalVariantIds = additionalVariantIds;
            reusedSearch = false;
        } else {
            // This is just used and necessary for pagination.
            result = currentState;
            result.page = response.page;
            result.rows = response.rows;
            result.totalPages = response.totalPages;
            // union is actually stable (documentation calls this results being in order)!
            result.resultIds = _.union(currentState.resultIds, response.resultIds);
            result.additionalVariantIds = _.union(currentState.additionalVariantIds, additionalVariantIds);
            // will override old values with new, but retain previous results.
            result.results = Object.assign(currentState.results, response.results);
            reusedSearch = true;
        }
        result.idSearchPosition = mapToCountedIds(result.resultIds);
        return { result, reusedSearch };
    }

    _queryArticles() {
        let queryIds = [] as { idToFetch: string; variantId: string }[];
        const result = state.result;
        if (result && result.resultIds && result.resultIds.length > 0) {
            queryIds = enhanceWithOfferInfo(result.resultIds.concat(result.additionalVariantIds), result.results);
        } else if (result) {
            result.result = [];
        }

        const variantLookupMap = {} as { [variantId: string]: string };
        queryIds.forEach((id) => {
            variantLookupMap[id.variantId] = id.idToFetch;
        });
        ArticleStore.fetchArticlesWithPreselection(
            queryIds.map((id) => id.idToFetch),
            variantLookupMap
        );
    }

    _handleArticleUpdate() {
        const result = state.result;
        if (result && result.results) {
            const mappedArticles = this._mapSearchArticles(result, "SearchResult");
            result.result = _.compact(mappedArticles);
            state.gotArticles = true;
            this.trigger(state);
        }
    }

    _mapSearchArticles(result: SearchResultEx, type: "SearchResult"): Array<SearchResultItem> {
        return _.chain(result.resultIds)
            .map((id) => {
                return _.get(result.results, id);
            })
            .compact()
            .map((r: SearchResultEntry) => {
                const idWithOffer = enhanceWithOfferInfo([r.id], result.results)[0].idToFetch;
                const articleStoreData = ArticleStore.getOneArticleData(idWithOffer);
                const customerStore = SessionStore.getCustomerStore();

                const successor = this._getSuccessorInDeliveryAssortment(r.successorVariantId, customerStore);

                // FIXME(FD)
                if (!articleStoreData) {
                    return {
                        state: "loading",
                        type: type,
                        id: idWithOffer
                    } as any;
                }
                if ("state" in articleStoreData) {
                    return {
                        state: articleStoreData.state,
                        type: type,
                        id: idWithOffer
                    } as any;
                }
                // TODO (DaBi): Make getBundleToShow more compatible to allow for both Bundle and BettyBundle as inputs to avoid
                //   explicitly casting to { [x: string]: Bundle; } (e.g. use Generics from TypeScript for that)
                const bundle = BundleSelectionUtil.getBundleToShow(
                    articleStoreData.bundles as { [x: string]: Bundle },
                    customerStore
                );

                const debugInfo =
                    r.score && r.indexingTime
                        ? {
                              score: r.score,
                              indexingTime: r.indexingTime,
                              explain: r.explain
                          }
                        : undefined;
                const item: SearchResultItem = {
                    state: "loaded",
                    type: type,
                    id: idWithOffer,
                    availability: _.get(bundle, ["customerAvailability"]),
                    variant: articleStoreData,
                    successor: successor,
                    onList: r.onList,
                    recentlyOrdered: r.recentlyOrdered,
                    position: result.idSearchPosition[r.id],
                    debugInfo,
                    ltrDebugInfo: r.ltrDebugInfo,
                    solrScore: r.score,
                    ropoRank: r.ropoRank
                };
                return item;
            })
            .value();
    }

    _getSuccessorInDeliveryAssortment(id: string, storeId: string | undefined | null) {
        const article = ArticleStore.getOneArticleData(id);
        if (!article || "state" in article) {
            return undefined;
        }
        const someAreDeliveryBundles = _.some(article.bundles, (bundle) => {
            const storeSettings = storeId ? bundle.stores[storeId] || {} : {};
            return storeSettings.delivery;
        });
        return someAreDeliveryBundles ? article : undefined;
    }

    _doSearchFailed() {
        TrackingUtil.trackUiErrorSd("SearchStore", "Search failed.", state.q);
        const notification = {
            type: "server-error" as const,
            key: "EX_SEARCH_ERROR",
            values: [],
            id: "EX_SEARCH_ERROR"
        };
        notify(notification);
        state.searchRunning = false;
        state.valid = false;
        this.trigger(state);
    }
}

export default new SearchStore();
